import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, Star, Share2, FileText, Check, Copy } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { formatNodeTitle } from '@/lib/pluralize'
import { hapticFeedback } from '@tma.js/sdk-react'

export default function ArticlePage() {
  const { lawCode, nodeId } = useParams<{ lawCode: string; nodeId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [shareToast, setShareToast] = useState(false)
  const [copyToast, setCopyToast] = useState(false)

  const { data: node, isLoading } = useQuery({
    queryKey: ['lawNode', lawCode, nodeId],
    queryFn: () => apiClient.getLawNode(lawCode!, nodeId!),
    enabled: !!lawCode && !!nodeId,
  })

  const { data: isFavorite } = useQuery({
    queryKey: ['isFavorite', nodeId],
    queryFn: () => apiClient.checkFavorite(nodeId!),
    enabled: !!nodeId,
  })

  const toggleFavorite = useMutation({
    mutationFn: async () => {
      if (isFavorite) {
        await apiClient.removeFavorite(nodeId!)
      } else {
        await apiClient.addFavorite(lawCode!, nodeId!, node?.title)
      }
    },
    onSuccess: () => {
      if (hapticFeedback.impactOccurred.isAvailable()) {
        hapticFeedback.impactOccurred('light')
      }
      queryClient.invalidateQueries({ queryKey: ['isFavorite', nodeId] })
      queryClient.invalidateQueries({ queryKey: ['favorites'] })
    },
  })

  const handleCopy = async () => {
    if (!node) return
    if (hapticFeedback.impactOccurred.isAvailable()) {
      hapticFeedback.impactOccurred('light')
    }
    const displayTitle = formatNodeTitle(node.level, node.num, node.title)
    const text = `${displayTitle}\n\n${node.content || ''}`
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    setCopyToast(true)
    setTimeout(() => setCopyToast(false), 2000)
  }

  const handleShare = async () => {
    if (!node) return
    if (hapticFeedback.impactOccurred.isAvailable()) {
      hapticFeedback.impactOccurred('light')
    }
    const displayTitle = formatNodeTitle(node.level, node.num, node.title)
    const text = `${displayTitle}\n\n${node.content?.substring(0, 200) || ''}`
    try {
      if (navigator.share) {
        await navigator.share({ title: displayTitle, text })
        return
      }
    } catch {
      // share API blocked or cancelled — fallback below
    }
    try {
      await navigator.clipboard.writeText(text)
      setShareToast(true)
      setTimeout(() => setShareToast(false), 2000)
    } catch {
      // clipboard also blocked — last resort
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      setShareToast(true)
      setTimeout(() => setShareToast(false), 2000)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-telegram-button/30 border-t-telegram-button" />
          <span className="text-sm text-telegram-hint">Загрузка статьи...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="article-page flex flex-col min-h-full">
      {/* Sticky header — Liquid Glass */}
      <div className="article-header sticky top-0 z-10 glass-header">
        <div className="flex items-center justify-between px-4 py-2.5">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-telegram-link active:opacity-60 transition-opacity"
          >
            <ChevronLeft size={18} />
            <span className="text-sm font-medium">Назад</span>
          </button>

          <div className="flex items-center gap-1">
            <button
              onClick={handleCopy}
              className="w-9 h-9 flex items-center justify-center rounded-xl glass-icon active:scale-90 transition-all"
            >
              <Copy size={16} className="text-telegram-hint" />
            </button>
            <button
              onClick={() => toggleFavorite.mutate()}
              className="w-9 h-9 flex items-center justify-center rounded-xl glass-icon active:scale-90 transition-all"
            >
              <Star
                size={16}
                className={isFavorite ? 'fill-yellow-400 text-yellow-400' : 'text-telegram-hint'}
              />
            </button>
            <button
              onClick={handleShare}
              className="w-9 h-9 flex items-center justify-center rounded-xl glass-icon active:scale-90 transition-all"
            >
              <Share2 size={16} className="text-telegram-hint" />
            </button>
          </div>
        </div>
      </div>

      {/* Article content */}
      {node ? (
        <article className="article-body px-3 py-4 glass-appear">
          <div className="article-container glass-surface p-4">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl glass-icon flex items-center justify-center shrink-0 mt-0.5">
                <FileText size={17} className="text-telegram-button" />
              </div>
              <div>
                <h1 className="text-base font-bold leading-snug">{formatNodeTitle(node.level, node.num, node.title)}</h1>
                {node.subtitle && (
                  <p className="text-xs text-telegram-hint mt-1">{node.subtitle}</p>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full glass-icon text-telegram-button">
                    {lawCode}
                  </span>
                </div>
              </div>
            </div>

            <div className="h-px bg-telegram-hint/10 my-4" />

            <div className="text-sm leading-relaxed text-telegram-text whitespace-pre-wrap">
              {node.content
                ? node.content
                : <span className="text-telegram-hint">Текст статьи отсутствует (возможно, норма утратила силу или ещё не загружена).</span>}
            </div>
          </div>
        </article>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full glass-icon flex items-center justify-center mb-4">
            <FileText size={28} className="text-telegram-hint/50" />
          </div>
          <p className="text-sm text-telegram-hint">Статья не найдена</p>
        </div>
      )}

      {/* Toasts — Liquid Glass */}
      {(copyToast || shareToast) && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2.5 glass-toast text-telegram-text text-sm font-medium animate-fade-in">
          <Check size={16} />
          {copyToast ? 'Скопировано' : 'Ссылка скопирована'}
        </div>
      )}
    </div>
  )
}
